import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

// Exported so the dev UI review page can supply a fake login
export const KickAuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_KICK_OAUTH_SERVICE || 'http://localhost:4000';
const SEND_SERVICE_URL = process.env.REACT_APP_KICK_CHAT_SEND_SERVICE;
const IS_DEV = process.env.NODE_ENV === 'development';
// In development a dev access token can stand in for a real Kick login
const DEV_TOKEN = IS_DEV ? process.env.REACT_APP_KICK_DEV_ACCESS_TOKEN : undefined;
// Skip the tab-focus status check if we checked this recently (a logged-out tab rechecks sooner,
// so a login made in another tab shows up, but not on every window switch)
const AUTH_CHECK_TTL_LOGGED_IN = 5 * 60 * 1000;
const AUTH_CHECK_TTL_LOGGED_OUT = 30 * 1000;
// Left behind by older versions, which kept the viewer's identity in sessionStorage
const LEGACY_STORAGE_KEYS = ['kick_user_id', 'kick_username', 'kick_user_identity', 'kick_user_info'];

// Headers for kick-chat-send-service requests
export const kickSendHeaders = () => (DEV_TOKEN ? { 'X-Dev-Token': DEV_TOKEN } : {});

// Kick scopes the UI checks. Moderators grant the moderation ones separately (kickModerationLoginUrl),
// and Kick's consent screen lets anyone untick them, so check what was granted, not who the viewer is.
export const KICK_SCOPES = {
  ban: 'moderation:ban',
  deleteMessage: 'moderation:chat_message:manage',
};
const ALL_SCOPES = ['user:read', 'chat:write', KICK_SCOPES.ban, KICK_SCOPES.deleteMessage];
// What an OAuth service without scope reporting means: every login was granted this set
const LEGACY_SCOPES = ['user:read', 'chat:write', KICK_SCOPES.ban];

// Kick login that also asks for the moderation scopes, returning to the current page
export const kickModerationLoginUrl = () =>
  `${BACKEND_URL}/kick?moderation=1&redirect=${encodeURIComponent(window.location.href)}`;

// Single source of truth for the viewer's Kick login. The OAuth service only checks the session for a
// refresh token on /status; access tokens are minted and cached server-side when a service needs one,
// so there is nothing to refresh from the browser.
export const KickProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!DEV_TOKEN);
  const [isLoading, setIsLoading] = useState(!DEV_TOKEN);
  const [user, setUser] = useState(null); // { id: string, username: string } of the logged-in Kick account
  const [scopes, setScopes] = useState(DEV_TOKEN ? ALL_SCOPES : []); // what the viewer granted at login
  const lastAuthCheckRef = useRef(null); // { at, loggedIn } of the last completed status check
  const statusRequestRef = useRef(null); // one in-flight /status request shared by every caller

  const checkAuthStatus = useCallback(() => {
    if (DEV_TOKEN) return Promise.resolve(true);
    if (!statusRequestRef.current) {
      statusRequestRef.current = fetch(`${BACKEND_URL}/kick/status`, { credentials: 'include' })
        .then((res) => (res.ok ? res.json() : { isLoggedIn: false }))
        .catch((err) => {
          console.error('[Kick] Auth status check failed:', err);
          return { isLoggedIn: false };
        })
        .then((data) => {
          const loggedIn = !!data.isLoggedIn;
          setIsLoggedIn(loggedIn);
          setScopes(!loggedIn ? [] : Array.isArray(data.scopes) ? data.scopes : LEGACY_SCOPES);
          if (!loggedIn) setUser(null);
          lastAuthCheckRef.current = { at: Date.now(), loggedIn };
          setIsLoading(false);
          return loggedIn;
        })
        .finally(() => {
          statusRequestRef.current = null;
        });
    }
    return statusRequestRef.current;
  }, []);

  const logout = useCallback(async () => {
    lastAuthCheckRef.current = { at: Date.now(), loggedIn: false };
    if (!DEV_TOKEN) {
      await fetch(`${BACKEND_URL}/kick/logout`, { method: 'POST', credentials: 'include' });
    }
    setIsLoggedIn(false);
    setUser(null);
    setScopes([]);
  }, []);

  // Initial check (StrictMode's double mount shares the same request)
  useEffect(() => {
    LEGACY_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Who is logged in: fetched once per login rather than once per chat
  useEffect(() => {
    if (!isLoggedIn || !SEND_SERVICE_URL) {
      setUser(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${SEND_SERVICE_URL}/user`, { withCredentials: true, headers: kickSendHeaders() })
      .then((res) => {
        const account = res.data?.data?.[0];
        if (!cancelled && account?.user_id) {
          setUser({ id: String(account.user_id), username: account.name });
        }
      })
      .catch((err) => {
        console.error('[Kick] Failed to fetch the logged-in user:', err.response?.data || err.message);
        // The session may have ended server-side; let /status decide
        if (!cancelled && err.response?.status === 401) checkAuthStatus();
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, checkAuthStatus]);

  // Re-check when the tab becomes visible (throttled), and always when the browser restores this page
  // from its back/forward cache: that page's state is a snapshot from before the user navigated away,
  // so it can show a login that has since ended (or miss one that has since started).
  useEffect(() => {
    if (DEV_TOKEN) return undefined;
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const last = lastAuthCheckRef.current;
      const ttl = last?.loggedIn ? AUTH_CHECK_TTL_LOGGED_IN : AUTH_CHECK_TTL_LOGGED_OUT;
      if (last && Date.now() - last.at < ttl) return;
      checkAuthStatus();
    };
    const handlePageShow = (event) => {
      if (event.persisted) checkAuthStatus();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [checkAuthStatus]);

  const hasScope = useCallback((scope) => scopes.includes(scope), [scopes]);

  const contextValue = useMemo(
    () => ({ isLoggedIn, isLoading, user, scopes, hasScope, checkAuthStatus, logout }),
    [isLoggedIn, isLoading, user, scopes, hasScope, checkAuthStatus, logout]
  );

  return (
    <KickAuthContext.Provider value={contextValue}>
      {children}
    </KickAuthContext.Provider>
  );
};

// Hook for components
export const useKickAuth = () => {
  const context = useContext(KickAuthContext);
  if (!context) {
    throw new Error('useKickAuth must be used within a KickProvider');
  }
  return context;
};

// The logged-in viewer's identity in one channel: badges that make them a subscriber, moderator or broadcaster there
export const useKickChannelIdentity = (channelId) => {
  const { user } = useKickAuth();
  const [identity, setIdentity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIdentity(null);
    if (!channelId || !user) {
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    axios
      .get(`https://kick.com/api/v2/channels/${channelId}/users/${user.id}/identity`)
      .then((res) => {
        if (!cancelled) setIdentity(res.data?.data?.identity || null);
      })
      .catch((err) => console.error('[Kick] Failed to fetch channel identity:', err.message))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channelId, user]);

  return { identity, isLoading };
};
